const ASROSYNC_JWT_KEY = 'asrosync-jwt';
const ASROSYNC_TENANT_ID_KEY = 'asrosync-tenant-id';
const ASROSYNC_API_BASE_URL_KEY = 'asrosync-api-base-url';
const ASROSYNC_SOURCE_TENANT_KEY = 'asrosync-source-tenant-id';
const TOKEN_REFRESH_SKEW_MS = 60_000;

let memoryJwt = null;
let memoryTenantId = null;
let memoryApiBase = null;
let memorySourceTenantId = null;
let memoryExpiresAt = 0;
let tokenInFlight = null;

const getBaseUrl = () => process.env.REACT_APP_BACKEND_URL || '';

const conductorHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

const readJwtClaim = (token, ...keys) => {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const json = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    for (const key of keys) {
      const value = json?.[key];
      if (value != null && String(value).trim()) return String(value).trim();
    }
    return null;
  } catch {
    return null;
  }
};

/**
 * Conductor tenant key for asrosync_connection / tenant_link / Chat Hub.
 * Prefer session tenant (localStorage.tenant_id) — same pattern as the rest of Conductor —
 * so Profile tenant switches apply. Fall back to JWT only when session tenant is missing.
 */
export const getConductorTenantId = () => {
  const fromLs = localStorage.getItem('tenant_id');
  if (fromLs && String(fromLs).trim()) {
    return String(fromLs).trim();
  }
  const token = localStorage.getItem('token');
  return token
    ? readJwtClaim(token, 'tenantKey', 'tenant_key', 'tenantId', 'tenant_id')
    : null;
};

export const getConductorUserId = () => {
  const token = localStorage.getItem('token');
  const fromJwt = token
    ? readJwtClaim(token, 'userId', 'user_id', 'sub', 'username')
    : null;
  if (fromJwt) return fromJwt;
  const fromLs =
    localStorage.getItem('userId') ||
    localStorage.getItem('username') ||
    localStorage.getItem('email');
  return fromLs && String(fromLs).trim() ? String(fromLs).trim() : null;
};

export const getConductorUserName = () => {
  const token = localStorage.getItem('token');
  const fromJwt = token
    ? readJwtClaim(token, 'username', 'userName', 'email', 'sub')
    : null;
  if (fromJwt) return fromJwt;
  const fromLs =
    localStorage.getItem('username') ||
    localStorage.getItem('userName') ||
    localStorage.getItem('email');
  return fromLs && String(fromLs).trim() ? String(fromLs).trim() : null;
};

const normalizeBaseUrl = (url) => {
  if (!url) return null;
  const trimmed = String(url).trim();
  return trimmed.endsWith('/') ? trimmed : `${trimmed}/`;
};

const readJwtExpiryMs = (token) => {
  try {
    const payload = token.split('.')[1];
    if (!payload) return 0;
    const json = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    if (!json?.exp) return 0;
    return Number(json.exp) * 1000;
  } catch {
    return 0;
  }
};

const cacheSession = ({ jwt, tenantId, apiBaseUrl, sourceTenantId }) => {
  if (jwt) {
    memoryJwt = jwt;
    memoryExpiresAt = readJwtExpiryMs(jwt) || Date.now() + 50 * 60 * 1000;
    sessionStorage.setItem(ASROSYNC_JWT_KEY, jwt);
  }
  if (tenantId != null && String(tenantId).trim()) {
    memoryTenantId = String(tenantId).trim();
    sessionStorage.setItem(ASROSYNC_TENANT_ID_KEY, memoryTenantId);
  }
  if (apiBaseUrl) {
    memoryApiBase = normalizeBaseUrl(apiBaseUrl);
    sessionStorage.setItem(ASROSYNC_API_BASE_URL_KEY, memoryApiBase);
  }
  if (sourceTenantId != null && String(sourceTenantId).trim()) {
    memorySourceTenantId = String(sourceTenantId).trim();
    sessionStorage.setItem(ASROSYNC_SOURCE_TENANT_KEY, memorySourceTenantId);
  }
};

const hydrateFromSession = () => {
  if (!memoryJwt) {
    memoryJwt = sessionStorage.getItem(ASROSYNC_JWT_KEY);
    memoryExpiresAt = memoryJwt ? readJwtExpiryMs(memoryJwt) : 0;
  }
  if (!memoryTenantId) {
    memoryTenantId = sessionStorage.getItem(ASROSYNC_TENANT_ID_KEY);
  }
  if (!memoryApiBase) {
    memoryApiBase = normalizeBaseUrl(sessionStorage.getItem(ASROSYNC_API_BASE_URL_KEY));
  }
  if (!memorySourceTenantId) {
    memorySourceTenantId = sessionStorage.getItem(ASROSYNC_SOURCE_TENANT_KEY);
  }
};

const isTokenFresh = () =>
  Boolean(memoryJwt) && memoryExpiresAt > Date.now() + TOKEN_REFRESH_SKEW_MS;

export const clearAsrosyncSession = () => {
  memoryJwt = null;
  memoryTenantId = null;
  memoryApiBase = null;
  memorySourceTenantId = null;
  memoryExpiresAt = 0;
  tokenInFlight = null;
  sessionStorage.removeItem(ASROSYNC_JWT_KEY);
  sessionStorage.removeItem(ASROSYNC_TENANT_ID_KEY);
  sessionStorage.removeItem(ASROSYNC_API_BASE_URL_KEY);
  sessionStorage.removeItem(ASROSYNC_SOURCE_TENANT_KEY);
};

/** After Connect — cache JWT / Sync tenant / API base only (never client secret). */
export const setAsrosyncConnectionSettings = ({
  apiBaseUrl,
  tenantId,
  jwtToken,
  sourceTenantId,
}) => {
  cacheSession({
    jwt: jwtToken,
    tenantId,
    apiBaseUrl,
    sourceTenantId: sourceTenantId || getConductorTenantId(),
  });
};

/** Conductor source tenant for agentContext (Connect key / JWT tenantKey). */
export const getCachedSourceTenantId = () => {
  hydrateFromSession();
  return memorySourceTenantId || getConductorTenantId();
};

export const getAsrosyncTenantId = () => {
  hydrateFromSession();
  return memoryTenantId || null;
};

export const getAsrosyncApiBase = () => {
  hydrateFromSession();
  return memoryApiBase || '';
};

export const getAsrosyncToken = () => {
  hydrateFromSession();
  return memoryJwt;
};

export const getAsrosyncClaims = () => {
  const token = getAsrosyncToken();
  if (!token) return null;
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    return JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
  } catch {
    return null;
  }
};

export const getAsrosyncUserId = () => {
  const claims = getAsrosyncClaims();
  return claims?.sub || claims?.userId || localStorage.getItem('userId') || null;
};

/**
 * Ensures a valid Sync machine token via Conductor BFF.
 * Credentials stay on the server; UI only caches short-lived JWT + tenant/api base.
 */
export const ensureAsrosyncAuth = async ({ force = false } = {}) => {
  hydrateFromSession();
  if (!force && isTokenFresh() && memoryTenantId && memoryApiBase) {
    return {
      jwt: memoryJwt,
      tenantId: memoryTenantId,
      apiBaseUrl: memoryApiBase,
      status: 'CONNECTED',
    };
  }

  if (!force && tokenInFlight) {
    return tokenInFlight;
  }

  const conductorTenantId = getConductorTenantId();
  if (!conductorTenantId) {
    throw new Error('Tenant context missing. Please sign in again.');
  }

  tokenInFlight = (async () => {
    const res = await fetch(
      `${getBaseUrl()}conductor/asrosync/token?tenantId=${encodeURIComponent(conductorTenantId)}`,
      { headers: conductorHeaders() }
    );
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(
        data?.message || data?.error || `Failed to get AsroSync token (${res.status})`
      );
    }
    const jwt = data?.jwt || data?.accessToken || data?.access_token;
    if (!jwt) {
      throw new Error('No access token returned from AsroSync connection');
    }
    cacheSession({
      jwt,
      tenantId: data?.tenantId || data?.targetTenantId,
      apiBaseUrl: data?.apiBaseUrl,
      sourceTenantId: data?.sourceTenantId || conductorTenantId,
    });
    if (!memoryApiBase) {
      throw new Error('AsroSync API base URL missing. Reconnect under External Integration.');
    }
    return {
      jwt: memoryJwt,
      tenantId: memoryTenantId,
      apiBaseUrl: memoryApiBase,
      sourceTenantId: memorySourceTenantId,
      status: data?.status || 'CONNECTED',
    };
  })();

  try {
    return await tokenInFlight;
  } finally {
    tokenInFlight = null;
  }
};

export const isAsrosyncConnected = async () => {
  try {
    const conductorTenantId = getConductorTenantId();
    if (!conductorTenantId) return false;
    const res = await fetch(
      `${getBaseUrl()}conductor/asrosync/connection?tenantId=${encodeURIComponent(conductorTenantId)}`,
      { headers: conductorHeaders() }
    );
    if (!res.ok) return false;
    const data = await res.json();
    return Boolean(
      data?.clientId && String(data?.status || '').toUpperCase() === 'CONNECTED'
    );
  } catch {
    return false;
  }
};

export const getAsrosyncAuthHeaders = (extra = {}) => {
  hydrateFromSession();
  const headers = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    ...extra,
  };
  if (memoryJwt) {
    headers.Authorization = `Bearer ${memoryJwt}`;
  }
  if (memoryTenantId) {
    headers['X-Tenant-Id'] = memoryTenantId;
  }
  const userId = getAsrosyncUserId();
  if (userId) {
    headers['X-User-Id'] = userId;
  }
  try {
    headers['X-User-Timezone'] = Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    // ignore
  }
  return headers;
};

export const getAsrosyncAuthHeadersAsync = async (extra = {}) => {
  await ensureAsrosyncAuth();
  return getAsrosyncAuthHeaders(extra);
};
